import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../components/ui'
import { useAuthFetch } from '../lib/useAuthFetch'

const API = import.meta.env.VITE_API_URL ?? ''

interface PlanPreview {
  billing: {
    billing_status: string
    billing_exempt: boolean
    trial_ends_at: string | null
    billing_period_end: string | null
    billing_cancel_at_period_end: boolean
  }
  locations: Array<{ location_id: string; location_name: string; rival_allowance: number | null; tracked_rivals: number }>
}

function statusCopy(preview: PlanPreview): { title: string; detail: string } {
  const { billing_status: status, trial_ends_at: trialEnd, billing_period_end: periodEnd } = preview.billing
  if (status === 'trial_ended') {
    return {
      title: 'Your free trial has ended',
      detail: trialEnd
        ? `Your 14-day trial ended ${new Date(trialEnd).toLocaleDateString()}. Choose a monthly plan to continue.`
        : 'Choose a monthly plan to continue using CannaSpy.',
    }
  }
  if (status === 'past_due') {
    return { title: 'Payment needs attention', detail: 'Update your payment method to restore access.' }
  }
  if (status === 'canceling') {
    return {
      title: 'Your subscription is ending',
      detail: periodEnd
        ? `Your paid access runs through ${new Date(periodEnd).toLocaleDateString()}. Your locations and rival choices remain saved.`
        : 'Your locations and rival choices remain saved.',
    }
  }
  if (status === 'canceled') {
    return { title: 'Your subscription has ended', detail: 'Your locations, rival choices, and blocks remain saved. Choose plans to restart.' }
  }
  return { title: 'Choose your location plans', detail: 'Select a monthly rival allowance for every active location to continue.' }
}

export default function Offboarded() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()
  const [preview, setPreview] = useState<PlanPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await authFetch(`${API}/api/v1/billing/plan-preview`)
      const body = await response.json()
      if (!response.ok || !body?.success) throw new Error(body?.error || 'Could not load your account status.')
      const next = body.data as PlanPreview
      if (next.billing.billing_exempt || ['active', 'trial_active', 'trial_pending'].includes(next.billing.billing_status)) {
        navigate('/today', { replace: true })
        return
      }
      setPreview(next)
    } catch (loadError: any) {
      setError(loadError?.message || 'Could not load your account status.')
    } finally {
      setLoading(false)
    }
  }, [authFetch, navigate])

  useEffect(() => { void load() }, [load])

  const copy = preview ? statusCopy(preview) : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 20px' }}>
      <div style={{ width: '100%', maxWidth: 620 }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 25, textAlign: 'center', color: 'var(--text-1)', marginBottom: 22 }}>CannaSpy</div>
        <Card style={{ padding: '28px 30px', textAlign: 'center' }}>
          {loading ? (
            <div style={{ color: 'var(--text-2)' }}>Loading your account status…</div>
          ) : error ? (
            <>
              <div style={{ fontFamily: 'var(--display)', fontSize: 25, color: 'var(--text-1)', marginBottom: 10 }}>We couldn't load billing</div>
              <div role="alert" style={{ color: 'var(--text-2)', lineHeight: 1.6 }}>{error}</div>
              <button className="btn" onClick={() => void load()} style={{ marginTop: 18 }}>Try again</button>
            </>
          ) : copy && preview ? (
            <>
              <div style={{ fontFamily: 'var(--display)', fontSize: 28, color: 'var(--text-1)', marginBottom: 10 }}>{copy.title}</div>
              <div style={{ color: 'var(--text-2)', lineHeight: 1.65, maxWidth: 480, margin: '0 auto' }}>{copy.detail}</div>
              <div style={{ marginTop: 18, padding: 14, borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 12 }}>
                {preview.locations.length} active location{preview.locations.length === 1 ? '' : 's'} and your saved rival selections are still available.
              </div>
              <button className="btn" onClick={() => navigate('/billing')} style={{ marginTop: 18 }}>Review plans and billing</button>
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-3)' }}>Monthly plans only. A free trial never converts or charges automatically.</div>
            </>
          ) : null}
        </Card>
      </div>
    </div>
  )
}
